/**
 * Mapa em tela cheia. Cria a instância única do OpenLayers, liga as camadas
 * GOES e a foto de fundo (depois do zoom máximo do GOES) e registra o mapa em
 * `useMapInstance` (contrato).
 */
import { useEffect, useRef } from 'react';
import { frameCache } from '../goes/frameCache';
import { setGoesManager } from '../loop/render';
import { useViewStore } from '../state/view';
import { createMap } from './createMap';
import { GoesLayerManager } from './goesLayers';
import { FundoLayerManager } from './fundoLayer';
import { FundoSemNoaa } from './fundoSemNoaa';
import { iniciarMotorDetalhe } from '../detalhe/motor';
import { useMapInstance } from './mapContext';
import { flyHome, startViewSync } from './viewControl';

export function MapView() {
  const mapRef = useRef<HTMLDivElement>(null);
  const attributionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { initialCenter, initialZoom } = useViewStore.getState();
    const fromLink = initialCenter !== null || initialZoom !== null;
    const map = createMap({
      target: mapRef.current!,
      attributionTarget: attributionRef.current!,
      initialCenter,
      initialZoom,
    });
    const manager = new GoesLayerManager(map, frameCache);
    const fundo = new FundoLayerManager(map);
    // Sem a imagem de base (a NOAA parou): a foto ao fundo também nos zooms afastados.
    const fundoSemNoaa = new FundoSemNoaa(map);
    // Detalhe em blocos (servidor na máquina do agendador): a nitidez da área visível.
    const pararDetalhe = iniciarMotorDetalhe(map);
    setGoesManager(manager);
    const stopViewSync = startViewSync(map);
    let raf = 0;
    if (fromLink) {
      useViewStore.setState({ isHome: false });
    } else {
      // Espera o layout (barra de tempo) para descontar os painéis.
      raf = requestAnimationFrame(() => flyHome(map, 0));
    }
    useMapInstance.getState().setMap(map);

    return () => {
      cancelAnimationFrame(raf);
      useMapInstance.getState().setMap(null);
      stopViewSync();
      setGoesManager(null);
      pararDetalhe();
      manager.dispose();
      fundoSemNoaa.dispose();
      fundo.dispose();
      map.setTarget(undefined);
      map.dispose();
    };
  }, []);

  return (
    <>
      <div
        ref={mapRef}
        className="amaview-map absolute inset-0 bg-ink-950"
        role="region"
        aria-label="Mapa com a imagem do satélite GOES-19"
      />
      {/* Focável: no celular o crédito fica numa linha e se abre ao tocar (index.css). */}
      <div ref={attributionRef} className="amaview-attribution" tabIndex={0} aria-label="Créditos da foto de fundo (toque para ver todos)" />
    </>
  );
}
